import { useState } from 'react';
import { Plus, Trash2, AlertCircle, Rocket } from 'lucide-react';
import { useTranslations } from '../../i18n';
import { theme } from '../../lib/theme';
import { Link } from 'react-router-dom';

type AllowedEmailsSectionProps = {
  ownerEmail?: string;
  allowedEmails: string[];
  onChange: (emails: string[]) => void;
  isEmbedded?: boolean;
  isPro?: boolean;
  isAdminView?: boolean;
};

export function AllowedEmailsSection({ ownerEmail, allowedEmails, onChange, isEmbedded = false, isPro = false, isAdminView = false }: AllowedEmailsSectionProps) {
  const { t } = useTranslations();
  const [newEmail, setNewEmail] = useState('');
  const [error, setError] = useState<string | null>(null);

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleAddEmail = () => {
    const emailToAdd = newEmail.trim().toLowerCase();

    if (!emailToAdd) return;

    if (!isPro && !isAdminView) {
      setError((t.cars as any).proOnly || 'Pro only');
      return;
    }

    if (!isValidEmail(emailToAdd)) {
      setError((t.common as any).invalidEmail || 'Invalid email format');
      return;
    }

    if (allowedEmails.includes(emailToAdd) || emailToAdd === ownerEmail?.toLowerCase()) {
      setError((t.cars as any).emailAlreadyAdded || 'This email is already allowed for this car.');
      return;
    }

    // Check maximum limit (e.g., 5 allowed emails per car)
    if (allowedEmails.length >= 5) {
      setError((t.cars as any).maxAllowedEmailsReached || 'Maximum limit of allowed emails reached.');
      return;
    }

    setError(null);
    const updatedEmails = [...allowedEmails, emailToAdd];
    onChange(updatedEmails);
    setNewEmail('');
  };

  const handleRemoveEmail = (emailToRemove: string) => {
    setError(null);
    const updatedEmails = allowedEmails.filter(email => email !== emailToRemove);
    onChange(updatedEmails);
  };

  return (
    <div className={isEmbedded ? "pt-4 border-t border-gray-50 dark:border-white/5 bg-transparent" : "bg-white dark:bg-slate-900/40 border border-gray-100 dark:border-white/10 rounded-2xl p-4 shadow-sm mt-4"}>
      {!isEmbedded && (
        <div className="flex items-center justify-between mb-4">
          <h3 className={theme.typography.sectionLabel}>{(t.cars as any).allowedSenderEmails || 'Allowed Sender Emails'}</h3>
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-xl text-sm flex items-center gap-2">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {/* Pro Restriction Alert */}
      {!isPro && !isAdminView && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-900/30 rounded-2xl flex items-start gap-4">
          <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full flex items-center justify-center shrink-0">
            <Rocket size={20} />
          </div>
          <div className="flex-1">
            <p className="text-sm font-bold text-red-800 dark:text-red-300 mb-1 leading-tight">
              {(t.cars as any).proFeatureEmailTitle || 'Pro Feature'}
            </p>
            <p className="text-xs font-medium text-red-600 dark:text-red-400/80 mb-2 leading-relaxed">
              {(t.cars as any).proFeatureEmailDesc || 'Upgrade to unlock this feature.'}
            </p>
            <Link
              to="/settings/plans"
              className="inline-flex items-center text-xs font-black text-red-700 dark:text-red-300 hover:underline"
            >
              {t.settings.upgradePlan || 'Upgrade Plan'} →
            </Link>
          </div>
        </div>
      )}

      <div className="space-y-3 mb-4">
        {ownerEmail && (
          <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-slate-800/50 rounded-xl border border-gray-100 dark:border-white/5 opacity-80">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                {ownerEmail}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {(t.cars as any).ownerEmail || 'OwnerEmail'} ({(t.cars as any).defaultAllowed || 'Always allowed'})
              </p>
            </div>
          </div>
        )}

        {allowedEmails.map(email => (
          <div key={email} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-slate-800/50 rounded-xl border border-gray-100 dark:border-white/5">
            <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
              {email}
            </span>
            <button
              onClick={() => handleRemoveEmail(email)}
              type="button"
              className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
              title={(t.common as any).remove || 'Remove'}
            >
              <Trash2 size={16} />
            </button>
          </div>
        ))}

        {allowedEmails.length === 0 && !ownerEmail && (
          <p className="text-sm text-gray-500 italic text-center py-2">{(t.cars as any).noAllowedEmails || 'No allowed emails added.'}</p>
        )}
      </div>

      <div className="flex gap-2 text-sm">
        <input
          type="email"
          value={newEmail}
          onChange={(e) => {
            setNewEmail(e.target.value);
            setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAddEmail();
            }
          }}
          placeholder={(t.cars as any).addAllowedEmailPlaceholder || 'Add allowed email...'}
          className="flex-1 px-4 py-2.5 bg-gray-50 dark:bg-slate-800/50 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all text-sm text-gray-900 dark:text-white placeholder:text-gray-400 disabled:opacity-50"
          disabled={(allowedEmails.length >= 5) || (!isPro && !isAdminView)}
        />
        <button
          onClick={handleAddEmail}
          type="button"
          disabled={!newEmail.trim() || (allowedEmails.length >= 5) || (!isPro && !isAdminView)}
          className="px-4 py-2.5 bg-blue-600 dark:bg-blue-500 text-white rounded-xl font-bold hover:bg-blue-700 dark:hover:bg-blue-400 transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          <Plus size={16} />
          <span className="hidden sm:inline">{(t.common as any).add || 'Add'}</span>
        </button>
      </div>
      {!isEmbedded && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
          {(t.cars as any).allowedEmailsDesc || 'Add email addresses that are allowed to send receipts to this car. Maximum 5 addresses.'}
        </p>
      )}
    </div>
  );
}

